import React, { useState, useEffect } from 'react';

const AccountLinking = () => {
  const [linkedAccounts, setLinkedAccounts] = useState({});
  const [availableProviders, setAvailableProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(null);

  // Mock data - replace with actual API calls
  const mockProviders = [
    {
      id: 'google',
      name: 'Google',
      description: 'Connect with Google Calendar and Gmail',
      icon: 'https://developers.google.com/identity/images/g-logo.png',
      features: ['Calendar sync', 'Email notifications', 'Profile import'],
      connected: true,
      connectedAt: new Date('2024-01-15'),
      lastSync: new Date(Date.now() - 30 * 60 * 1000),
      syncStatus: 'success'
    },
    {
      id: 'facebook',
      name: 'Facebook',
      description: 'Share events and import friends',
      icon: 'https://facebook.com/favicon.ico',
      features: ['Friend import', 'Event sharing', 'Page management'],
      connected: true,
      connectedAt: new Date('2024-02-10'),
      lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000),
      syncStatus: 'success'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      description: 'Share event photos and stories',
      icon: 'https://instagram.com/favicon.ico',
      features: ['Photo sharing', 'Story posting', 'Business profile'],
      connected: false
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      description: 'Professional networking and event promotion',
      icon: 'https://linkedin.com/favicon.ico',
      features: ['Professional networking', 'Business events', 'Company page'],
      connected: false
    },
    {
      id: 'twitter',
      name: 'Twitter',
      description: 'Tweet about events and engage with community',
      icon: 'https://twitter.com/favicon.ico',
      features: ['Event tweets', 'Community engagement', 'Live updates'],
      connected: false
    },
    {
      id: 'apple',
      name: 'Apple',
      description: 'Sync with Apple Calendar and Contacts',
      icon: 'https://apple.com/favicon.ico',
      features: ['Calendar sync', 'Contact import', 'Apple Pay integration'],
      connected: false
    },
    {
      id: 'eventbrite',
      name: 'Eventbrite',
      description: 'Import events and attendee data',
      icon: 'https://eventbrite.com/favicon.ico',
      features: ['Event import', 'Attendee sync', 'Ticket integration'],
      connected: true,
      connectedAt: new Date('2024-03-05'),
      lastSync: new Date(Date.now() - 12 * 60 * 60 * 1000),
      syncStatus: 'warning',
      syncMessage: 'Some events failed to sync'
    },
    {
      id: 'zoom',
      name: 'Zoom',
      description: 'Create and manage virtual events',
      icon: 'https://zoom.us/favicon.ico',
      features: ['Virtual events', 'Meeting scheduling', 'Recording access'],
      connected: false
    }
  ];

  useEffect(() => {
    fetchLinkedAccounts();
  }, []);

  const fetchLinkedAccounts = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      // const response = await api.get('/api/user/linked-accounts');
      // setLinkedAccounts(response.data.accounts);
      // setAvailableProviders(response.data.providers);
      
      const accounts = {};
      const connectedProviders = mockProviders.filter(p => p.connected);
      connectedProviders.forEach(provider => {
        accounts[provider.id] = provider;
      });
      
      setLinkedAccounts(accounts);
      setAvailableProviders(mockProviders);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const connectAccount = async (providerId) => {
    setConnecting(providerId);
    try {
      // In a real implementation, this would:
      // 1. Open OAuth popup/redirect
      // 2. Handle OAuth callback
      // 3. Store tokens securely
      
      // For demo, simulate OAuth flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Replace with actual OAuth flow
      // window.location.href = `/api/auth/oauth/${providerId}`;
      
      // Mock successful connection
      const provider = availableProviders.find(p => p.id === providerId);
      const updatedProvider = {
        ...provider,
        connected: true,
        connectedAt: new Date(),
        lastSync: new Date(),
        syncStatus: 'success'
      };
      
      setLinkedAccounts(prev => ({
        ...prev,
        [providerId]: updatedProvider
      }));
      
      setAvailableProviders(prev => 
        prev.map(p => p.id === providerId ? updatedProvider : p)
      );
      
    } catch (error) {    } finally {
      setConnecting(null);
    }
  };

  const disconnectAccount = async (providerId) => {
    try {
      // Replace with actual API call
      // await api.delete(`/api/user/linked-accounts/${providerId}`);
      
      setLinkedAccounts(prev => {
        const updated = { ...prev };
        delete updated[providerId];
        return updated;
      });
      
      setAvailableProviders(prev => 
        prev.map(p => p.id === providerId ? { ...p, connected: false } : p)
      );
      
      setShowConfirmDisconnect(null);
    } catch (error) {    }
  };

  const syncAccount = async (providerId) => {
    try {
      // Replace with actual API call
      // await api.post(`/api/user/linked-accounts/${providerId}/sync`);
      
      // Update last sync time
      setLinkedAccounts(prev => ({
        ...prev,
        [providerId]: {
          ...prev[providerId],
          lastSync: new Date(),
          syncStatus: 'success'
        }
      }));
    } catch (error) {    }
  };

  const getSyncStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return (
          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatLastSync = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Account Linking</h2>
          <p className="text-gray-600">
            Connect your social media and productivity accounts to enhance your CliQQ experience.
            Sync calendars, share events, and import your network.
          </p>
        </div>

        {/* Connected Accounts */}
        {Object.keys(linkedAccounts).length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-green-700">Connected Accounts</h3>
            <div className="space-y-4">
              {Object.values(linkedAccounts).map((account) => (
                <div key={account.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <img
                      src={account.icon}
                      alt={account.name}
                      className="w-8 h-8 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{account.name}</h4>
                        {getSyncStatusIcon(account.syncStatus)}
                      </div>
                      <p className="text-sm text-gray-600">{account.description}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-500">
                          Connected {account.connectedAt.toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500">
                          Last sync: {formatLastSync(account.lastSync)}
                        </span>
                      </div>
                      {account.syncMessage && (
                        <p className="text-xs text-yellow-600 mt-1">{account.syncMessage}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => syncAccount(account.id)}
                      className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                      title="Sync now"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowConfirmDisconnect(account.id)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available Accounts */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Available Integrations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableProviders.filter(provider => !provider.connected).map((provider) => (
              <div key={provider.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-4">
                  <img
                    src={provider.icon}
                    alt={provider.name}
                    className="w-8 h-8 rounded"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{provider.name}</h4>
                    <p className="text-sm text-gray-600">{provider.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {provider.features.map((feature, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => connectAccount(provider.id)}
                  disabled={connecting === provider.id}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-400"
                >
                  {connecting === provider.id ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-cyan-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-cyan-900">Privacy & Security</h4>
              <p className="text-sm text-cyan-800 mt-1">
                We use industry-standard OAuth 2.0 for secure account linking. We only access the data you explicitly 
                authorize and never store your account passwords. You can disconnect any account at any time.
              </p>
              <div className="mt-2">
                <a href="/privacy-policy" className="text-sm text-cyan-600 hover:text-cyan-800 underline">
                  Learn more about our privacy practices
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmDisconnect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Disconnect Account</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to disconnect your {linkedAccounts[showConfirmDisconnect]?.name} account? 
              This will stop syncing data and remove integration features.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => disconnectAccount(showConfirmDisconnect)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
              >
                Yes, Disconnect
              </button>
              <button
                onClick={() => setShowConfirmDisconnect(null)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountLinking;